import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import {
  CustomResultOfPaginatedListOfGetVoiceMessageGroupsWithPaginationDto,
  GetVoiceMessageGroupsWithPaginationDto,
  PaginatedListOfGetVoiceMessageGroupsWithPaginationDto,
} from 'src/app/core/api/models';
import { VoiceMessageGroupsService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { ConsoleService } from 'src/app/core/services/console.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { BaseIndexPageComponent } from '../../shared/base';
import { VoiceMessagesGroupFormComponent } from './voice-messages-group-form/voice-messages-group-form.component';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { VoiceMessagesComponent } from "../voice-messages/voice-messages.component";

@Component({
    selector: 'app-voice-messages-group',
    templateUrl: './voice-messages-group.component.html',
    imports: [BaseIndexModule, VoiceMessagesComponent]
})
export class VoiceMessagesGroupComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties
  pageName = 'voiceMessagesGroup';

  // Inject service
  protected currentService = inject(VoiceMessageGroupsService);

  // Refactored properties
  paginationApiMethodName: string = this.currentService.voiceMessageGroupsGetVoiceMessageGroupsWithPagination.name;
  modelWithPagination$: Observable<PaginatedListOfGetVoiceMessageGroupsWithPaginationDto>;
  items$: Observable<Array<GetVoiceMessageGroupsWithPaginationDto>>;
  selectedItem: {};

  //#endregion

  constructor(
    public cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.pageConfig.showAuditBtn = false;
  }

  configTableColumns(): void {
    this.tableColumns = [
      {
        title: this.localizer.id,
        name: 'id',
        hasSearch: true,
        type: 'number',
      },
      {
        title: this.localizer.title,
        name: 'title',
        hasSearch: true,
        type: 'text',
      },
    ];
  }

  bindTableData() {
    if (this.isBindTableData) return;
    this.isBindTableData = true;
    this.tableDataSubject$
      .pipe(
        switchMap((res) => {
          this.pageConfig.loading = true;
          return this.getPaginationApi().pipe(
            map((res) => res.data),
            tap((result) => {
              this.modelWithPagination$ = of(result);
            }),
            finalize(() => (this.pageConfig.loading = false)),
            catchError((error) => {
              this.pageConfig.isEmpty = false;
              this.pageConfig.hasError = true;
              return error;
            }),
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.pageConfig.loading = false;
            })
          );
        })
      )
      .subscribe({
        next: (res: any) => {
          this.checkSelectedItems();
          this.getPaginationData(res);
          this.items = res.items;
          this.selectedItem = { ...this.items[0] };

          this.items$ = of(this.items);
          res.items?.length > 0
            ? (this.pageConfig.isEmpty = false)
            : (this.pageConfig.isEmpty = true);
          this.pageConfig.loading = false;
        },
        error: (error) => {
          ConsoleService.error('error', error);
          this.isBindTableData = false;
        },
      });
  }

    //Delete
    baseDeleteApi(id: number) {
      this.unSubs();
  
      this.pageConfig.loading = true;
      this.lastSelectedItemId = id;
  
      this.currentService
        .voiceMessageGroupsDelete({ body: { id: id } })
        .pipe(this.basePipeOperators())
        .subscribe(this.baseObserver);
    }

  setDefaultTableSort() {
    try {
      //اگر از قبل مقدار وجود داشت دیگه push نکنه
      const isExistsSortObject = this.sortObject.some((item) =>
        item.columnName.includes('id')
      );
      if (!isExistsSortObject)
        this.sortObject.push({ columnName: 'id', sort: 1 });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  //#region api

  //#endregion

  onCreate(type?: any, isFormModal?: boolean) {
    try {
      if (isFormModal) {
        const timeout = setTimeout(() => {
          this.pageConfig.isVisibleForm = false;
          clearTimeout(timeout);
        }, 10);
        this.openCreateFormModal();
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  //#region call Actions

  openCreateFormModal() {
    try {
      this.unSubs();

      let data = {
        title: `${this.formPageConfig?.vocabs}addTitle`,
        isEdit: false,
      };

      const modalResult = this.dialogService.openModal({
        component: VoiceMessagesGroupFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.pageConfig.isVisibleForm = undefined)),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  onUpdate(item) {
    this.showParent = false;
    this.setParentQueryString();

    this.formPageConfig.editData = {};
    this.formPageConfig.isEditMode = true;
    this.openEditFormModal(item);
  }

  openEditFormModal(model: GetVoiceMessageGroupsWithPaginationDto) {
    try {
      this.unSubs();

      let data = {
        title: `${this.formPageConfig?.vocabs}editTitle`,
        model: { title: model.title, id: model.id },
        isEdit: true,
      };

      const modalResult = this.dialogService.openModal({
        component: VoiceMessagesGroupFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  //#endregion

  onSelect(model: GetVoiceMessageGroupsWithPaginationDto) {
    try {
      this.selectedItem = {};
      this.selectedItem = {
        id: model.id,
        title: model.title,
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }
}

